import { FieldArray, Formik } from "formik";
import React, { useMemo, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { IconButton, Button } from '@blueprint/ui';
import { FormatOptionLabelContext } from "react-select/src/Select";
import * as yup from "yup";

import { UserResponsePayload } from "../../../api/project";
import { Loader } from "../../../common/components/core";
import {
    FormContainer,
    FormFooter,
    InputField,
    SelectField,
} from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { FormSubmissionHandler, Party } from "../../../types/common";

import {
    ClaimApproversTooltip,
    PaymentOrderApproversTooltip,
} from "./ProjectFormTooltip";
import { UserStatus } from "../../../types/users";
export type FormApprover = {
    firstName: string;
    lastName: string;
    email: string;
    userProfileName: string;
};

export type FormApproverDetails = {
    approver: string;
    roleName?: string;
};

export type ProjectFormValues = {
    name: string;
    id: string;
    contractor: string[];
    claimApprovers: FormApproverDetails[];
    paymentOrderApprovers: FormApproverDetails[];
};
const schema: yup.SchemaOf<ProjectFormValues> = yup.object().shape({
    name: yup.string().required(),
    id: yup.string().required(),
    contractor: yup.array().min(1),
    claimApprovers: yup.array().of(
        yup.object().shape({
            approver: yup.string().required(),
            roleName: yup.string(),
        }),
    ),
    paymentOrderApprovers: yup.array().of(
        yup.object().shape({
            approver: yup.string().required(),
            roleName: yup.string(),
        }),
    ),
});

type ProjectFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<ProjectFormValues>;
    contractors: Party[] & {
        disabled?: boolean;
    };
    claimApprovers: UserResponsePayload[];
    paymentOrderApprovers: UserResponsePayload[];
    isEdit: boolean;
    initialValues: {
        name: string;
        id: string;
        contractor: string[];
        claimApprovers: FormApproverDetails[];
        paymentOrderApprovers: FormApproverDetails[];
    };
};

const ApproverOptionLabelContent = (
    option: SelectOption,
    context: FormatOptionLabelContext,
) => {
    const detail: FormApprover = JSON.parse(option.value);
    return (
        <div>
            {context === "menu" ? (
                <div>
                    <div className="d-flex align-items-center">
                        <span className="fw-bold">{option.label} </span>
                        <span className="fw-bold">
                            ({detail.userProfileName})
                        </span>
                    </div>
                    <div className="fw-normal text-label-grey text-truncate">{`${detail.email}`}</div>
                </div>
            ) : (
                <div className="d-flex align-items-center">
                    <span className="fw-bold">{option.label} </span>
                    <span className="fw-bold text-truncate">{`(${detail.email})`}</span>
                </div>
            )}
        </div>
    );
};

export const initialApprover: FormApproverDetails = {
    approver: "",
    roleName: "",
};

const ProjectForm = ({
    onCancel,
    onSubmit,
    contractors,
    claimApprovers,
    paymentOrderApprovers,
    isEdit,
    initialValues,
}: ProjectFormProps): JSX.Element => {
    const contractorOptions = useMemo(() => {
        return contractors.map((it: any) => ({ value: it.accountName, label: it.name, isDisabled: it.disabled }));
    }, [contractors]);
    const claimApproverOptions: SelectOption[] = useMemo(() => {
        const activeClaimApprovers = claimApprovers.filter(
            (it) => it.status === UserStatus.ACTIVE,
        );
        return activeClaimApprovers.map((it) => ({
            value: JSON.stringify({
                firstName: it.firstName,
                lastName: it.lastName,
                email: it.email,
                userProfileName: it.profileName,
            }),
            label: `${it.firstName} ${it.lastName}`,
        }));
    }, [claimApprovers]);

    const paymentOrderApproverOptions: SelectOption[] = useMemo(() => {
        const activePaymentOrderApprovers = paymentOrderApprovers.filter(
            (it) => it.status === UserStatus.ACTIVE,
        );
        return activePaymentOrderApprovers.map((it) => ({
            value: JSON.stringify({
                firstName: it.firstName,
                lastName: it.lastName,
                email: it.email,
                userProfileName: it.profileName,
            }),
            label: `${it.firstName} ${it.lastName}`,
        }));
    }, [paymentOrderApprovers]);

    const approverOptionsFilter = (
        fullApprovers: SelectOption[],
        selectedApprovers: FormApproverDetails[],
        selectFieldIndex: number,
    ): SelectOption[] => {
        return fullApprovers.filter((opt) => {
            const optEmail = JSON.parse(opt.value).email;
            const selected = selectedApprovers[selectFieldIndex].approver;
            const selectedEmail = !!selected ? JSON.parse(selected).email: null;
            if (optEmail === selectedEmail) {
                return true;
            }
            const selectedApproverEmail = selectedApprovers.map((it) => !!it.approver ? JSON.parse(it.approver).email : {});
            return !selectedApproverEmail.includes(optEmail);
        });
    };
    const defaultApproverOptions = (
        fullApprovers: SelectOption[],
        selectedApprovers: FormApproverDetails[],
        selectFieldIndex: number,
    ): SelectOption | undefined => {
        return fullApprovers.find((opt, index) => {
            const optEmail = JSON.parse(opt.value).email;
            const selected = selectedApprovers[selectFieldIndex].approver;
            const selectedEmail = !!selected ? JSON.parse(selected).email: null;
            if (optEmail === selectedEmail) {
                return true;
            }
        });
    };
    const [claimIndex, setClaimIndex] = useState<(number | null)>(null);
    const [paymentIndex, setPaymentIndex] = useState<(number | null)>(null);

    useEffect(() => {
        if (claimIndex !== null) {
            const ele = document.getElementById(`claimApprovers[${claimIndex}].approver`);
            if (ele !== null) {
                const input = ele.querySelector('input');
                input && input.focus();
            }
        }
    }, [claimIndex]);
    useEffect(() => {
        if (paymentIndex !== null) {
            const ele = document.getElementById(`paymentOrderApprovers[${paymentIndex}].approver`);
            if (ele !== null) {
                const input = ele.querySelector('input');
                input && input.focus();
            }
        }
    }, [paymentIndex]);

    const setFocus = (target: [number, string]) => {
        if (target[1] === 'payment') {
            setPaymentIndex(target[0]);
        }
        if (target[1] === 'claim') {
            setClaimIndex(target[0]);
        }
    };
    return (
        <Formik
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSubmit}
            initialValues={initialValues}
        >
            {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
                <Loader isLoading={isSubmitting}>
                    <FormContainer>
                        <h1 className="mb-3 rem-1-5">
                            {isEdit ? (
                                <>Edit Details for Your New Project</>
                            ) : (
                                <>Enter New Project Details</>
                            )}
                        </h1>
                        <Form
                            autoComplete="off"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <InputField
                                name="name"
                                label="Project Name"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue("name", values.name.trim())
                                }
                                autoFocus
                                required={true}
                            />
                            <InputField
                                name="id"
                                label="Project ID"
                                type="text"
                                maxLength={20}
                                required={true}
                            />
                            <hr className="bg-border-grey my-3" />
                            <h1 className="mb-3 rem-1-5">
                                {isEdit ? "Edit" : "Select"}{" "}
                                <span>
                                    {" "}
                                    Service Provider
                                </span>
                            </h1>
                            <SelectField
                                isMulti={true}
                                name="contractor"
                                label="Project's Service Provider"
//                                 disabled={isEdit}
                                options={contractorOptions}
                                required={true}
                            />
                            <hr className="bg-border-grey my-3" />
                            <h1 className="mb-3 rem-1-5">
                                {isEdit ? "Edit" : "Select"}{" "}
                                <span>
                                    Claim Approvers
                                </span>
                                <ClaimApproversTooltip />
                            </h1>
                            <FieldArray
                                name="claimApprovers"
                                render={(arrayHelpers) => (
                                    <div className="mb-5">
                                        {values.claimApprovers.map(
                                            (claimApprover, index) => (
                                                <Row
                                                    className="align-items-center"
                                                    key={index}
                                                >
                                                    <Col>
                                                        <SelectField
                                                            className="break-word"
                                                            name={`claimApprovers[${index}].approver`}
                                                            label="Approver"
                                                            options={approverOptionsFilter(
                                                                claimApproverOptions,
                                                                values.claimApprovers,
                                                                index,
                                                            )}
                                                            formatOptionLabel={(
                                                                option,
                                                                { context },
                                                            ) => {
                                                                return ApproverOptionLabelContent(
                                                                    option,
                                                                    context,
                                                                );
                                                            }}
//                                                             required={true}
                                                            defaultValue={defaultApproverOptions(
                                                                claimApproverOptions,
                                                                values.claimApprovers,
                                                                index,
                                                            )}
                                                        />
                                                    </Col>
                                                    <Col className={'input-box'}>
                                                        <InputField
                                                            className="mb-0"
                                                            name={`claimApprovers[${index}].roleName`}
                                                            label="Role Name"
                                                            type="text"
                                                            maxLength={64}
                                                        />
                                                    </Col>
                                                    <Col xs={1}>
                                                        <IconButton
                                                            icon={"Delete"}
                                                            variant="light.tertiary"
                                                            size="small"
//                                                             disabled={values.claimApprovers.length <= 1}
                                                            aria-label={`delete claim approver`}
                                                            onClick={() =>
                                                                arrayHelpers.remove(
                                                                    index,
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            ),
                                        )}
                                        {values.claimApprovers.length < 15 && (
                                            <IconButton
                                                icon={"Add"}
                                                variant="light.tertiary"
                                                disabled={values.claimApprovers.length >= 15}
                                                onClick={() => {
                                                    arrayHelpers.push(
                                                        initialApprover,
                                                    );
                                                    setFocus([values.claimApprovers.length, "claim"]);
                                                }}
                                            >Add Approver</IconButton>
                                        )}
                                    </div>
                                )}
                            />
                            <hr className="bg-border-grey my-3" />
                            <h1 className="mb-3 rem-1-5">
                                {isEdit ? "Edit" : "Select"}{" "}
                                <span>
                                    Payment Order Approvers
                                </span>
                                <PaymentOrderApproversTooltip />
                            </h1>
                            <FieldArray
                                name="paymentOrderApprovers"
                                render={(arrayHelpers) => (
                                    <div>
                                        {values.paymentOrderApprovers.map(
                                            (paymentOrderApprover, index) => (
                                                <Row
                                                    className="align-items-center"
                                                    key={index}
                                                >
                                                    <Col>
                                                        <SelectField
                                                            className="break-word"
                                                            name={`paymentOrderApprovers[${index}].approver`}
                                                            label="Approver"
                                                            options={approverOptionsFilter(
                                                                paymentOrderApproverOptions,
                                                                values.paymentOrderApprovers,
                                                                index,
                                                            )}
                                                            formatOptionLabel={(
                                                                option,
                                                                { context },
                                                            ) => {
                                                                return ApproverOptionLabelContent(
                                                                    option,
                                                                    context,
                                                                );
                                                            }}
                                                            required={true}
                                                            defaultValue={defaultApproverOptions(
                                                                paymentOrderApproverOptions,
                                                                values.paymentOrderApprovers,
                                                                index,
                                                            )}
                                                        />
                                                    </Col>
                                                    <Col className={'input-box'}>
                                                        <InputField
                                                            name={`paymentOrderApprovers[${index}].roleName`}
                                                            label="Role Name"
                                                            type="text"
                                                            maxLength={64}
                                                        />
                                                    </Col>
                                                    <Col xs={1}>
                                                        <IconButton
                                                            icon={"Delete"}
                                                            variant="light.tertiary"
                                                            size="small"
                                                            disabled={values.paymentOrderApprovers.length <= 1}
                                                            aria-label={`delete payment order approver`}
                                                            onClick={() =>
                                                                arrayHelpers.remove(
                                                                    index,
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            ),
                                        )}
                                        <IconButton
                                            icon={"Add"}
                                            variant="light.tertiary"
                                            disabled={values.paymentOrderApprovers.length >= 15}
                                            onClick={() => {
                                                arrayHelpers.push(
                                                    initialApprover,
                                                );
                                                setFocus([values.paymentOrderApprovers.length, "payment"]);
                                            }}
                                        >Add Approver</IconButton>
                                    </div>
                                )}
                            />
                            <hr className="bg-border-grey my-3" />
                            <FormFooter className="d-flex justify-content-between">
                                <Button
                                    variant="light.tertiary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button variant="light.primary" type="submit">
                                    {isEdit ? "Save" : "Create"}
                                </Button>
                            </FormFooter>
                        </Form>
                    </FormContainer>
                </Loader>
            )}
        </Formik>
    );
};

export default ProjectForm;
